import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL } from '@cnt-workspace/data-access';
import { IPayout, IPayoutSummary } from '@curbnturf/entities';
import { objectToUrlParams } from '@curbnturf/helpers';
import { Observable } from 'rxjs';

const API_URL = BASE_API_URL + 'user/financial/payouts';

export interface IAdminPayoutPage {
  total: number;
  page: number;
  payouts: IPayout[];
}

/** Columns `GET .../payouts/paginated` sorts by (the payout table's sortable headers). */
export type AdminPayoutSort =
  | 'requestedOn'
  | 'status'
  | 'isCntCash'
  | 'requestedBy.firstName'
  | 'requestedPhone'
  | 'requestedAmount'
  | 'paidByMethod'
  | 'note';

/** Payout requests (server AdminAuthGuard). Replaces @curbnturf/user-dashboard's FinancialService for admin. */
@Injectable({ providedIn: 'root' })
export class AdminPayoutApiService {
  private readonly http = inject(HttpClient);

  /** Every payout with this status (all when empty), unpaged — for the CSV export. */
  list(status?: string): Observable<IPayout[]> {
    return this.http.get<IPayout[]>(status ? `${API_URL}?status=${encodeURIComponent(status)}` : API_URL);
  }

  /** One page of payouts with this status (all when empty), sorted on the server. */
  page(
    status: string | undefined,
    page: number,
    pageSize: number,
    sort: AdminPayoutSort = 'requestedOn',
    dir: 'asc' | 'desc' = 'desc',
  ): Observable<IAdminPayoutPage> {
    const query = objectToUrlParams({ status: status || undefined, page, pageSize, sort, dir });
    return this.http.get<IAdminPayoutPage>(`${API_URL}/paginated?${query}`);
  }

  /** One user's payout requests, any status (`GET .../payouts/:userId`; admins may read anyone's). */
  forUser(userId: number): Observable<IPayout[]> {
    return this.http.get<IPayout[]>(`${API_URL}/${userId}`);
  }

  /** How many payouts have this status, and their requested total. */
  summary(status: string): Observable<IPayoutSummary> {
    return this.http.get<IPayoutSummary>(`${API_URL}/summary?status=${encodeURIComponent(status)}`);
  }

  /** Records the admin's response (paid/declined/pending); the server emails the user only for paid by check or deposit. */
  complete(payout: IPayout): Observable<IPayout> {
    return this.http.put<IPayout>(`${API_URL}/${payout.id}`, payout);
  }
}
